<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { usePlayerStore, type PlayerTrack } from '~/stores/player'
import { createGrowthEventId } from '~/utils/growthEvents'
import { sendSongEvent, songRequest } from '~/services/songPlayback'
import SongRow from './SongRow.vue'
import { visiblePlays, sourceLabels } from './discovery-source.mjs'
import SongMenu from './SongMenu.vue'
import MmIcon from '../base/MmIcon.vue'

const props = defineProps<{ songId: string }>()
const { t } = useI18n()
const player = usePlayerStore()
const root = ref<HTMLElement | null>(null)
const items = ref<PlayerTrack[]>([])
let requestId = '', epoch = 0, observer: IntersectionObserver | undefined
const context = (position: number) => ({ surface: 'song_page' as const, requestId, position })
const click = (song: PlayerTrack, index: number) => { void sendSongEvent(song.id, 'click', context(index + 1)) }
const play = (song: PlayerTrack, index: number) => {
  click(song, index)
  if (player.currentTrack?.id === song.id) player.toggle()
  else player.play(song, context(index + 1), { queue: items.value })
}

const load = async () => {
  const current = ++epoch
  observer?.disconnect(); items.value = []; requestId = createGrowthEventId()
  try {
    const data = await songRequest('music/public/v1/music/similar', { id: props.songId, limit: 8 })
    if (current !== epoch) return
    items.value = Array.isArray(data?.items) ? data.items : []
    await nextTick()
    if (current !== epoch) return
    observer = new IntersectionObserver(entries => {
      for (const entry of entries) if (entry.isIntersecting && entry.intersectionRatio >= .5) {
        const index = Number((entry.target as HTMLElement).dataset.index)
        const song = items.value[index]
        if (song) void sendSongEvent(song.id, 'impression', context(index + 1))
        observer?.unobserve(entry.target)
      }
    }, { threshold: .5 })
    root.value?.querySelectorAll('[data-index]').forEach(row => observer?.observe(row))
  } catch { /* Optional recommendations are hidden on failure. */ }
}
onMounted(load)
watch(() => props.songId, load)
onBeforeUnmount(() => { ++epoch; observer?.disconnect() })
</script>

<template>
  <section v-if="items.length" ref="root" class="card song-similar-list" aria-labelledby="similar-songs-title">
    <div class="between"><h3 id="similar-songs-title" class="t-h3">{{ t('mm.song.moreLikeThis') }}</h3><NuxtLink to="/music" class="see-all">{{ t('common.more') }}<MmIcon name="right" :size="16" /></NuxtLink></div>
    <div v-for="(song, index) in items" :key="song.id" :data-index="index">
      <SongRow home-compact inline-play :title="song.title || ''" :artist="song.creator?.display_name || t('ux.creatorUnavailable')" :src="song.image_url" :to="`/music/song/${song.id}`"
        :duration="song.metadata?.duration" :tags="song.metadata?.tags?.split(',') || []" :plays="visiblePlays(song)" :source-labels="sourceLabels(song)"
        :state="player.currentTrack?.id === song.id ? player.loading ? 'loading' : player.playing ? 'playing' : 'idle' : 'idle'" cancelable @play="play(song, index)" @open="click(song, index)">
        <template #actions><SongMenu inline-actions :song="song" /></template>
      </SongRow>
    </div>
  </section>
</template>

<style scoped>
.song-similar-list { min-width: 0; padding: 14px 10px; }
.between { padding: 0 8px 6px; }
.t-h3 { font-size: 17px; }
.see-all { min-height: 32px; }
</style>
